import { Link } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { Eyebrow, Reveal } from "../components/Reveal";
import { useContent } from "../content-context";

const SEO = {
  title: "About Collanmore Island Lodge — Private Island Experience in Clew Bay, Mayo",
  description:
    "The story of Collanmore Island Lodge: an exclusive-use private island for up to 22 guests in Clew Bay, Westport, County Mayo — privacy, adventure, relaxation and celebrations on the Wild Atlantic Way.",
  path: "/about-collanmore-island-lodge/",
};

export default function AboutPage() {
  const { about } = useContent();
  return (
    <PageLayout seo={SEO}>
      <section className="relative overflow-hidden pt-28 md:pt-36">
        <div className="mx-auto max-w-[900px] px-5 pb-16 md:px-10">
          <Reveal>
            <Eyebrow>{about.heroEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-6 font-display text-4xl font-medium leading-[1.05] text-navy md:text-6xl" data-testid="page-h1">
              {about.heroH1}
            </h1>
          </Reveal>
          {about.heroParas.map((p, i) => (
            <Reveal key={i} delay={0.14 + i * 0.06}>
              <p className="mt-5 max-w-2xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ivory pb-24 md:pb-32" data-testid="about-sections">
        <div className="mx-auto flex max-w-[1440px] flex-col gap-20 px-5 md:gap-28 md:px-10">
          {about.sections.map((s, i) => (
            <div key={i} className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
              <div className={i % 2 ? "lg:order-2" : ""}>
                <Reveal>
                  <Eyebrow>{s.eyebrow}</Eyebrow>
                </Reveal>
                <Reveal delay={0.08}>
                  <h2 className="mt-5 font-display text-3xl font-medium leading-tight text-navy md:text-4xl" data-testid={`about-h2-${i + 1}`}>
                    {s.h2}
                  </h2>
                </Reveal>
                {s.paras.map((p, j) => (
                  <Reveal key={j} delay={0.12 + j * 0.05}>
                    <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.15} className={i % 2 ? "lg:order-1" : ""}>
                <div className="aspect-[4/3] overflow-hidden" data-testid={`about-image-${i + 1}`}>
                  <img src={s.img} alt={s.alt} loading="lazy" className="h-full w-full object-cover" />
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-navy py-24 md:py-32" data-testid="about-story">
        <div className="mx-auto max-w-[900px] px-5 text-center md:px-10">
          <Reveal>
            <Eyebrow light>{about.storyEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-3xl font-medium leading-tight text-ivory md:text-5xl" data-testid="about-story-h2">
              {about.storyH2}
            </h2>
          </Reveal>
          {about.storyParas.map((p, i) => (
            <Reveal key={i} delay={0.14 + i * 0.06}>
              <p className="mt-6 text-sm font-light leading-relaxed text-ivory/80 md:text-base">{p}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-ivory py-24 md:py-32" data-testid="about-discover">
        <div className="mx-auto max-w-[900px] px-5 text-center md:px-10">
          <Reveal>
            <Eyebrow>{about.ctaEyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-3xl font-medium leading-tight text-navy md:text-5xl" data-testid="about-cta-h2">
              {about.ctaH2}
            </h2>
          </Reveal>
          {about.ctaParas.map((p, i) => (
            <Reveal key={i} delay={0.14}>
              <p className="mx-auto mt-5 max-w-xl text-sm font-light leading-relaxed text-navy/75 md:text-base">{p}</p>
            </Reveal>
          ))}
          <Reveal delay={0.24}>
            <Link
              to="/contact/"
              data-testid="about-cta"
              className="mt-9 inline-flex items-center gap-3 bg-navy px-9 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:bg-gold hover:text-navy"
            >
              {about.ctaLabel}
            </Link>
          </Reveal>
        </div>
      </section>
    </PageLayout>
  );
}
